// When this tab tells the server the user is here.
//
// The server ends an idle session on its own clock, and it cannot see input: polling, refreshes and
// draft saves are the app talking, not the user. So real input is reported, at most once a minute
// from each tab: the first input after a quiet minute goes at once, input after that waits for the
// minute to be up, and the last of it is reported once more so the server measures from it. Nothing
// goes while there is no input, so an unattended screen cannot keep itself signed in.
//
// A report that fails keeps the input it was for and tries again at the next boundary; it never
// moves when that input happened. "Stay signed in" is reported at once whatever the interval, and
// only the server's answer counts: the warning stays until it confirms.
//
// The interval is separate from the ten-second write of the shared input record
// (`useAutoLogoutTracker`), which only tells the other tabs.
export const ACTIVITY_REPORT_INTERVAL_MS = 60 * 1000;

// `stale`: the session the input belonged to is over in this tab, so there is nothing to report it to
export type ActivityReportOutcome = "acknowledged" | "failed" | "stale";

export interface ActivityReporter {
  noteInput: () => void;
  // Stay signed in: true once the server has confirmed it
  confirmPresence: () => Promise<boolean>;
  stop: () => void;
}

export const createActivityReporter = (
  send: () => Promise<ActivityReportOutcome>,
): ActivityReporter => {
  // input is counted rather than timed, so a report acknowledges exactly the input it was sent for
  let inputCount = 0;
  let reportedCount = 0;
  let lastSentAt: number | null = null;
  let inFlight = false;
  let confirming: Promise<boolean> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const report = async (): Promise<ActivityReportOutcome> => {
    const covers = inputCount;
    lastSentAt = Date.now();
    inFlight = true;
    const outcome = await send();
    inFlight = false;
    if (outcome === "acknowledged") reportedCount = Math.max(reportedCount, covers);
    // the session it belonged to is over: retrying would report it to whichever session comes next
    if (outcome === "stale") reportedCount = inputCount;
    schedule();
    return outcome;
  };

  function schedule() {
    if (stopped || timer || inFlight || reportedCount >= inputCount) return;
    const waitMs = lastSentAt === null ? 0 : lastSentAt + ACTIVITY_REPORT_INTERVAL_MS - Date.now();
    if (waitMs <= 0) {
      void report();
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      schedule();
    }, waitMs);
  }

  return {
    noteInput: () => {
      if (stopped) return;
      inputCount += 1;
      schedule();
    },
    confirmPresence: () => {
      if (stopped) return Promise.resolve(false);
      confirming ??= (async () => {
        inputCount += 1;
        if (timer) clearTimeout(timer);
        timer = null;
        const outcome = await report();
        confirming = null;
        return outcome === "acknowledged";
      })();
      return confirming;
    },
    stop: () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
};
